import { API, graphqlOperation } from "aws-amplify";
import * as mutations from "graphql/mutations";
import {
  listOneQueryOfCampaigns,
  listAllCampaigns,
  createCampaign,
  updateCampaign,
  createCampaignRequiredResolutions,
  createCampaignNativeResolutions,
  deleteCampaignRequiredResolutions,
  deleteCampaignNativeResolutions,
  getCampaign,
  createCampaignPrintSizes,
  deleteCampaignPrintSizes,
  sendInfoToCloudwatch,
  getActiveUser,
} from "services/apiMiddleware";
import { addOrUpdateArrayItem, replaceArrayItem } from "services/utils";
import config from "constants/config";
import _ from "lodash";
import { t } from "../../i18n";
import { ACTIVE_CAMPAIGNS_FILTER, INACTIVE_CAMPAIGNS_FILTER } from "constants/graphqlFilters";
import { ACTIVE_CAMPAIGN_STATUSES, INACTIVE_CAMPAIGN_STATUSES } from "constants/campaignStatuses";

const mergeActiveCampaignItems = (items, activeItems) =>
  _.concat(
    _.filter(items, (item) => _.includes(INACTIVE_CAMPAIGN_STATUSES, item.status)),
    activeItems
  );

const mergeInactiveCampaignItems = (items, inactiveItems) =>
  _.concat(
    _.filter(items, (item) => _.includes(ACTIVE_CAMPAIGN_STATUSES, item.status)),
    inactiveItems
  );

const appendActiveCampaignItems = (items, newActiveItems) =>
  _.concat(
    _.filter(items, (item) => _.includes(INACTIVE_CAMPAIGN_STATUSES, item.status)),
    _.filter(items, (item) => _.includes(ACTIVE_CAMPAIGN_STATUSES, item.status)),
    newActiveItems
  );

const appendInactiveCampaignItems = (items, newInactiveItems) =>
  _.concat(
    _.filter(items, (item) => _.includes(ACTIVE_CAMPAIGN_STATUSES, item.status)),
    _.filter(items, (item) => _.includes(INACTIVE_CAMPAIGN_STATUSES, item.status)),
    newInactiveItems
  );

const campaigns = {
  state: {
    isLoading: false,
    allIsLazyLoading: false,
    activeIsLazyLoading: false,
    inactiveIsLazyLoading: false,
    error: null,
    items: [],
    loadTime: 0,
    activeLoaded: false,
    inactiveLoaded: false,
    isCsvDataLoading: false,
    isCsvDataLoaded: false,
    csvDataItems: [],
  },
  reducers: {
    loadStart: (state) => {
      return {
        ...state,
        isLoading: true,
      };
    },
    loadAllSuccess: (state, items) => {
      return {
        ...state,
        isLoading: false,
        allIsLazyLoading: false,
        activeIsLazyLoading: false,
        inactiveIsLazyLoading: false,
        error: null,
        items,
        loadTime: Date.now(),
        activeLoaded: true,
        inactiveLoaded: true,
      };
    },
    loadAllLazyStart: (state) => {
      return {
        ...state,
        allIsLazyLoading: true,
      };
    },
    loadAllQueryDone: (state, items) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items,
        loadTime: Date.now(),
      };
    },
    loadAllLazyQueryDone: (state, items) => {
      return {
        ...state,
        error: null,
        items: _.concat(state.items, items),
        loadTime: Date.now(),
      };
    },
    loadAllLazySuccess: (state) => {
      return {
        ...state,
        error: null,
        allIsLazyLoading: false,
        activeLoaded: true,
        inactiveLoaded: true,
        loadTime: Date.now(),
      };
    },
    loadActiveLazyStart: (state) => {
      return {
        ...state,
        activeIsLazyLoading: true,
      };
    },
    loadActiveQueryDone: (state, items) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: mergeActiveCampaignItems(state.items, items),
        loadTime: Date.now(),
      };
    },
    loadActiveLazyQueryDone: (state, items) => {
      return {
        ...state,
        error: null,
        items: appendActiveCampaignItems(state.items, items),
        loadTime: Date.now(),
      };
    },
    loadActiveLazySuccess: (state) => {
      return {
        ...state,
        error: null,
        activeIsLazyLoading: false,
        activeLoaded: true,
        loadTime: Date.now(),
      };
    },
    loadActiveSuccess: (state, items) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        // items = previously loaded inactive campaigns and now loaded active campaigns
        items: _.concat(
          _.filter(state.items, (item) => _.includes(INACTIVE_CAMPAIGN_STATUSES, item.status)),
          items
        ),
        activeLoaded: true,
        loadTime: Date.now(),
      };
    },
    loadCsvActiveItemsStart: (state) => {
      return {
        ...state,
        isCsvDataLoading: true,
      };
    },
    loadCsvActiveItemsSuccess: (state, items) => {
      return {
        ...state,
        isCsvDataLoading: false,
        error: null,
        // items = previously loaded inactive campaigns and now loaded active campaigns
        csvDataItems: _.concat(
          _.filter(state.csvDataItems, (item) =>
            _.includes(INACTIVE_CAMPAIGN_STATUSES, item.status)
          ),
          items
        ),
        isCsvDataLoaded: true,
        loadTime: Date.now(),
      };
    },
    loadInactiveSuccess: (state, items) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        // items = previously loaded active campaigns and now loaded inactive campaigns
        items: _.concat(
          _.filter(state.items, (item) => _.includes(ACTIVE_CAMPAIGN_STATUSES, item.status)),
          items
        ),
        inactiveLoaded: true,
        loadTime: Date.now(),
      };
    },
    loadInactiveLazyStart: (state) => {
      return {
        ...state,
        inactiveIsLazyLoading: true,
      };
    },
    loadInactiveQueryDone: (state, items) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: mergeInactiveCampaignItems(state.items, items),
        loadTime: Date.now(),
      };
    },
    loadInactiveLazyQueryDone: (state, items) => {
      return {
        ...state,
        error: null,
        items: appendInactiveCampaignItems(state.items, items),
        loadTime: Date.now(),
      };
    },
    loadInactiveLazySuccess: (state) => {
      return {
        ...state,
        error: null,
        inactiveIsLazyLoading: false,
        inactiveLoaded: true,
        loadTime: Date.now(),
      };
    },
    loadOneSuccess: (state, foundItem) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: addOrUpdateArrayItem(state.items, (item) => item.id === foundItem.id, foundItem),
      };
    },
    createSuccess: (state, newItem) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: [newItem, ...state.items],
      };
    },
    updateSuccess: (state, updatedItem) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: replaceArrayItem(state.items, (item) => item.id === updatedItem.id, updatedItem),
      };
    },
    deleteSuccess: (state, deletedId) => {
      return {
        ...state,
        isLoading: false,
        error: null,
        items: state.items.filter((item) => item.id !== deletedId),
      };
    },
    failure: (state, error) => {
      return {
        ...state,
        isLoading: false,
        error,
      };
    },
    loadAllFailure: (state, error) => {
      return {
        ...state,
        isLoading: false,
        allIsLazyLoading: false,
        activeIsLazyLoading: false,
        inactiveIsLazyLoading: false,
        error,
        activeLoaded: false,
        inactiveLoaded: false,
      };
    },
    loadActiveFailure: (state, error) => {
      return {
        ...state,
        isLoading: false,
        activeIsLazyLoading: false,
        error,
        activeLoaded: false,
      };
    },
    loadCsvActiveItemsFailure: (state, error) => {
      return {
        ...state,
        isCsvDataLoading: false,
        error,
      };
    },
    loadInactiveFailure: (state, error) => {
      return {
        ...state,
        isLoading: false,
        inactiveIsLazyLoading: false,
        error,
        inactiveLoaded: false,
      };
    },
    stopLoading: (state) => {
      return {
        ...state,
        isLoading: false,
      };
    },
    resetCsvData: (state) => {
      return {
        ...state,
        isCsvDataLoading: false,
        isCsvDataLoaded: false,
        csvDataItems: [],
      };
    },
  },
  effects: (dispatch) => ({
    async loadAll(force, state) {
      try {
        if (
          !force &&
          Date.now() - state.loadTime < config.storeCacheMs &&
          state.campaigns.activeLoaded &&
          state.campaigns.inactiveLoaded
        )
          return;
        // Return if load is not forced and both active and inactive campaigns have already been loaded
        if (!force && state.campaigns.activeLoaded && state.campaigns.inactiveLoaded) return;
        dispatch.campaigns.loadStart();
        dispatch.campaigns.loadAllLazyStart();
        const activeResult = await listOneQueryOfCampaigns({ filter: ACTIVE_CAMPAIGNS_FILTER });
        dispatch.campaigns.loadActiveQueryDone(activeResult.data);
        let activeNextToken = activeResult.nextToken;
        while (activeNextToken) {
          const nextActiveResult = await listOneQueryOfCampaigns({
            filter: ACTIVE_CAMPAIGNS_FILTER,
            nextToken: activeNextToken,
          });
          dispatch.campaigns.loadActiveLazyQueryDone(nextActiveResult.data);
          activeNextToken = nextActiveResult.nextToken;
          if (nextActiveResult.errorMessage) console.warn(nextActiveResult.errorMessage);
        }

        const inactiveResult = await listOneQueryOfCampaigns({
          filter: INACTIVE_CAMPAIGNS_FILTER,
        });
        dispatch.campaigns.loadInactiveQueryDone(inactiveResult.data);
        let inactiveNextToken = inactiveResult.nextToken;
        while (inactiveNextToken) {
          const nextInactiveResult = await listOneQueryOfCampaigns({
            filter: INACTIVE_CAMPAIGNS_FILTER,
            nextToken: inactiveNextToken,
          });
          dispatch.campaigns.loadInactiveLazyQueryDone(nextInactiveResult.data);
          inactiveNextToken = nextInactiveResult.nextToken;
          if (nextInactiveResult.errorMessage) console.warn(nextInactiveResult.errorMessage);
        }

        dispatch.campaigns.loadAllLazySuccess();
        if (activeResult.errorMessage) console.warn(activeResult.errorMessage);
        if (inactiveResult.errorMessage) console.warn(inactiveResult.errorMessage);
      } catch (error) {
        console.error(error);
        dispatch.campaigns.loadAllFailure(t("campaigns_load_error", { ns: "common" }));
      }
    },
    async loadActive(force, state) {
      try {
        if (
          !force &&
          Date.now() - state.loadTime < config.storeCacheMs &&
          state.campaigns.activeLoaded
        )
          return;
        dispatch.campaigns.loadStart();
        dispatch.campaigns.loadActiveLazyStart();
        const result = await listOneQueryOfCampaigns({ filter: ACTIVE_CAMPAIGNS_FILTER });
        dispatch.campaigns.loadActiveQueryDone(result.data);
        let nextToken = result.nextToken;
        while (nextToken) {
          const nextResult = await listOneQueryOfCampaigns({
            filter: ACTIVE_CAMPAIGNS_FILTER,
            nextToken,
          });
          dispatch.campaigns.loadActiveLazyQueryDone(nextResult.data);
          nextToken = nextResult.nextToken;
          if (nextResult.errorMessage) console.warn(nextResult.errorMessage);
        }
        dispatch.campaigns.loadActiveLazySuccess();
        const { errorMessage } = result;
        if (errorMessage) console.warn(errorMessage);
      } catch (error) {
        console.error(error);
        dispatch.campaigns.loadActiveFailure(t("campaigns_load_error", { ns: "common" }));
      }
    },
    async loadCsvActiveItems(force, state) {
      try {
        if (!force && Date.now() - state.loadTime < config.storeCacheMs) return;
        // Return if load is not forced and active campaigns have already been loaded
        if (!force && state.campaigns.isCsvDataLoading) return;
        dispatch.campaigns.loadCsvActiveItemsStart();
        const { data, errorMessage } = await listAllCampaigns({
          filter: ACTIVE_CAMPAIGNS_FILTER,
        });
        dispatch.campaigns.loadCsvActiveItemsSuccess(data);
        if (errorMessage) console.warn(errorMessage);
      } catch (error) {
        console.error(error);
        dispatch.campaigns.loadCsvActiveItemsFailure(t("campaigns_load_error", { ns: "common" }));
      }
    },
    async loadInactive(force, state) {
      try {
        if (
          !force &&
          Date.now() - state.loadTime < config.storeCacheMs &&
          state.campaigns.inactiveLoaded
        )
          return;
        dispatch.campaigns.loadStart();
        dispatch.campaigns.loadInactiveLazyStart();
        const result = await listOneQueryOfCampaigns({ filter: INACTIVE_CAMPAIGNS_FILTER });
        dispatch.campaigns.loadInactiveQueryDone(result.data);
        let nextToken = result.nextToken;
        while (nextToken) {
          const nextResult = await listOneQueryOfCampaigns({
            filter: INACTIVE_CAMPAIGNS_FILTER,
            nextToken,
          });
          dispatch.campaigns.loadInactiveLazyQueryDone(nextResult.data);
          nextToken = nextResult.nextToken;
          if (nextResult.errorMessage) console.warn(nextResult.errorMessage);
        }
        dispatch.campaigns.loadInactiveLazySuccess();
        const { errorMessage } = result;
        if (errorMessage) console.warn(errorMessage);
      } catch (error) {
        console.error(error);
        dispatch.campaigns.loadInactiveFailure(t("campaigns_load_error", { ns: "common" }));
      }
    },
    async loadOne(id) {
      try {
        dispatch.campaigns.loadStart();
        const { data } = await getCampaign(id);
        dispatch.campaigns.loadOneSuccess(data);
        dispatch.campaigns.stopLoading();
        return data;
      } catch (error) {
        console.error(error);
        dispatch.campaigns.loadOneSuccess(undefined);
      }
    },
    async create({ createProps, requiredResolutions, nativeResolutions, printSizes }) {
      try {
        dispatch.campaigns.loadStart();
        const { data, errorMessage } = await createCampaign(createProps);
        if (errorMessage) console.warn(errorMessage);

        if (data && requiredResolutions && nativeResolutions) {
          await createCampaignRequiredResolutions(data.id, requiredResolutions);
          await createCampaignNativeResolutions(data.id, nativeResolutions);
        }

        if (data && printSizes) await createCampaignPrintSizes(data.id, printSizes);

        dispatch.campaigns.createSuccess(data);
        return data;
      } catch (error) {
        dispatch.campaigns.failure(t("campaign_form_campaign_creation_failed", { ns: "common" }));
        throw error;
      }
    },
    async update({
      updateProps,
      campaignRRidsToDelete,
      campaignNRidsToDelete,
      requiredResolutionsToAdd,
      nativeResolutionsToAdd,
      campaignPrintSizeIdsToDelete,
      printSizeIdsToAdd,
    }) {
      try {
        dispatch.campaigns.loadStart();
        const updateResult = await updateCampaign(updateProps);
        if (updateResult.errorMessage) console.warn(updateResult.errorMessage);

        // If campaign is ARCHIVED or CANCELLED send logs to cloudwatch
        if (updateResult.status === "ARCHIVED") {
          const UserUpdatingCampaign = await getActiveUser();
          await sendInfoToCloudwatch("archived-campaign", UserUpdatingCampaign, updateResult.data);
        }
        if (updateResult.status === "CANCELLED") {
          const UserUpdatingCampaign = await getActiveUser();
          await sendInfoToCloudwatch("cancelled-campaign", UserUpdatingCampaign, updateResult.data);
        }

        // Delete and create CampaignRequiredResolutions and CampaignNativeResolutions as necessary
        if (
          updateResult.data &&
          campaignRRidsToDelete &&
          campaignNRidsToDelete &&
          requiredResolutionsToAdd &&
          nativeResolutionsToAdd
        ) {
          await deleteCampaignRequiredResolutions(campaignRRidsToDelete);
          await deleteCampaignNativeResolutions(campaignNRidsToDelete);
          await createCampaignRequiredResolutions(updateProps.id, requiredResolutionsToAdd);
          await createCampaignNativeResolutions(updateProps.id, nativeResolutionsToAdd);
        }

        // Delete and create CampaignPrintSizes as necessary
        if (updateResult.data && campaignPrintSizeIdsToDelete && printSizeIdsToAdd) {
          await deleteCampaignPrintSizes(campaignPrintSizeIdsToDelete);
          await createCampaignPrintSizes(updateProps.id, printSizeIdsToAdd);
        }

        // Get updated campaign with resolution or print size changes
        const { data } = await getCampaign(updateProps.id);
        dispatch.campaigns.updateSuccess(data);
        return data;
      } catch (error) {
        dispatch.campaigns.failure(t("campaign_form_campaign_update_failed", { ns: "common" }));
        throw error;
      }
    },
    async updateStatus(createProps) {
      try {
        const { data, errorMessage } = await updateCampaign(createProps);
        // Get updated campaign with resolution or print size changes
        // - updateCampaign() request does not include @connections so getCampaign request is needed.
        const { data: updatedCampaign } = await getCampaign(data.id);
        dispatch.campaigns.updateSuccess(updatedCampaign);
        if (errorMessage) console.warn(errorMessage);
        return data;
      } catch (error) {
        dispatch.campaigns.failure(t("campaign_form_campaign_update_failed", { ns: "common" }));
        throw error;
      }
    },
    async delete(campaign) {
      const UserDeletingCampaign = await getActiveUser();
      try {
        const campaignData = await dispatch.campaigns.loadOne(campaign.id).then((response) => {
          return response;
        });
        const requiredResolutionsToDelete = await _.map(
          campaignData.requiredResolutions.items,
          "id"
        );
        const nativeResolutionsToDelete = await _.map(campaignData.nativeResolutions.items, "id");
        const printSizesToDelete = await _.map(campaignData.printSizes.items, "id");
        await deleteCampaignRequiredResolutions(requiredResolutionsToDelete);
        await deleteCampaignNativeResolutions(nativeResolutionsToDelete);
        await deleteCampaignPrintSizes(printSizesToDelete);
        const result = await API.graphql(
          graphqlOperation(mutations.deleteCampaign, { input: { id: campaign.id } })
        );
        dispatch.campaigns.loadStart();
        const deletedId = result.data.deleteCampaign.id;
        dispatch.campaigns.deleteSuccess(deletedId);
        // Send information to Cloudwatch
        await sendInfoToCloudwatch("delete-campaign", UserDeletingCampaign, campaignData);
      } catch (error) {
        dispatch.campaigns.failure(t("campaign_delete_failed", { ns: "common" }));
        throw error;
      }
    },
  }),
};

export default campaigns;
